import React, { useMemo } from 'react';
import {
  calculate05PercentIFF,
  calculate10PercentPV,
  calculate3PercentWAC,
  calculateAnnualBenefit,
  calculateNet,
  calculateTransferPrice,
  calculateUnitBenefit,
} from '../../bidAnalysisFormulas';
import classes from '../../bidAnalysisTemplate.module.scss';

interface WACAnalysisTableProps {
  formData?: any;
  packSizeFormData?: Record<number, any>;
  templateType?: string; // 'mas' | 'nc' - to distinguish between MAS and NC templates
}

export const WACAnalysisTable: React.FC<WACAnalysisTableProps> = ({
  formData,
  packSizeFormData,
  templateType,
}) => {
  const isNC = templateType?.toLowerCase() === 'nc';
  const formatValue = (value: string | number | undefined, isCurrency = false) => {
    if (value === null || value === undefined || value === '') return isCurrency ? '$0.00' : '0.00';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return isCurrency ? '$0.00' : '0.00';
    return isCurrency ? `$${num.toFixed(2)}` : num.toFixed(2);
  };

  const formatWholeNumber = (value: string | number | undefined) => {
    if (value === null || value === undefined || value === '') return '0';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return '0';
    return Math.round(num).toString();
  };

  // Get all pack size data - use packSizeFormData if available, otherwise fallback to formData
  // Sort by concept name, then by pack size
  const allPackSizes = useMemo(() => {
    let data: any[] = [];

    if (packSizeFormData && Object.keys(packSizeFormData).length > 0) {
      data = Object.values(packSizeFormData).filter(
        (packData: any) => packData && (packData.conceptName || packData.packSize)
      );
    } else if (formData && (formData.conceptName || formData.packSize)) {
      data = [formData];
    }

    // Sort by concept name, then by pack size (numeric)
    return data.sort((a: any, b: any) => {
      const conceptA = (a.conceptName || '').toLowerCase();
      const conceptB = (b.conceptName || '').toLowerCase();
      if (conceptA !== conceptB) {
        return conceptA.localeCompare(conceptB);
      }
      const packSizeA = parseFloat(a.mfgBottleSize || a.packSize || a.gsmsPackSize || '0');
      const packSizeB = parseFloat(b.mfgBottleSize || b.packSize || b.gsmsPackSize || '0');
      return packSizeA - packSizeB;
    });
  }, [packSizeFormData, formData]);

  // Calculate WAC Analysis values for each pack size
  const wacAnalysisData = useMemo(() => {
    return allPackSizes.map((packData: any) => {
      const wac = parseFloat(packData.wac || '0');
      const bidPrice = parseFloat(packData.suggestedBidPrice || '0');
      const gsmsNetCostFromMfg = parseFloat(packData.gsmsNetCostFromMfg || '0');
      const mfgBottleSize = parseFloat(packData.mfgBottleSize || packData.packSize || '0');
      const gsmsPackSize = parseFloat(packData.gsmsPackSize || packData.packSize || '0');
      // For NC template, use ncEstAnnualUsage; for MAS template, use targetAnnualUsage
      const annualUsage = isNC
        ? parseFloat(packData.ncEstAnnualUsage || '0')
        : parseFloat(packData.targetAnnualUsage || '0');
      const ndc = packData.selectedMfgNdc || packData.mfg_ndc || packData.gsms_mfg_ndc || 'N/A';

      // Calculate derived values
      const wac3Percent = calculate3PercentWAC(wac);
      const pv10Percent = calculate10PercentPV(bidPrice);
      const iff05Percent = calculate05PercentIFF(bidPrice);
      const net = calculateNet(bidPrice, wac3Percent, pv10Percent, iff05Percent);
      const transferPrice = calculateTransferPrice(gsmsNetCostFromMfg, mfgBottleSize, gsmsPackSize);
      const unitBenefit = calculateUnitBenefit(transferPrice, net);
      const annualBenefit = calculateAnnualBenefit(annualUsage, unitBenefit);

      return {
        sku: packData.conceptName || 'N/A',
        pk: gsmsPackSize,
        ndc: ndc,
        wac: wac,
        bidPrice: bidPrice,
        wac3Percent: wac3Percent,
        pv10Percent: pv10Percent,
        iff05Percent: iff05Percent,
        net: net,
        transferPrice: transferPrice,
        unitBenefit: unitBenefit,
        annualUsage: annualUsage,
        annualBenefit: annualBenefit,
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPackSizes]);

  if (wacAnalysisData.length === 0) {
    return (
      <div className={classes.globalTableWrapper}>
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className={classes.globalTableWrapper}>
      <table className={classes.globalTable}>
        <thead>
          <tr>
            <th>SKU</th>
            <th>PK</th>
            <th>NDC</th>
            <th>WAC</th>
            <th>BID PRICE</th>
            <th>3% WAC</th>
            <th>10% PV</th>
            <th>0.5% IFF</th>
            <th>NET</th>
            <th>TRANSFER PRICE</th>
            <th>UNIT BENEFIT</th>
            <th>ANNUAL USAGE</th>
            <th>ANNUAL BENEFIT</th>
          </tr>
        </thead>
        <tbody>
          {wacAnalysisData.map((row, index) => (
            <tr key={index}>
              <td>{row.sku}</td>
              <td>{formatWholeNumber(row.pk)}</td>
              <td className={classes.greenText}>{row.ndc}</td>
              <td>{formatValue(row.wac, true)}</td>
              <td>{formatValue(row.bidPrice, true)}</td>
              <td>{formatValue(row.wac3Percent, true)}</td>
              <td>{formatValue(row.pv10Percent, true)}</td>
              <td>{formatValue(row.iff05Percent, true)}</td>
              <td>{formatValue(row.net, true)}</td>
              <td>{formatValue(row.transferPrice, true)}</td>
              <td>{formatValue(row.unitBenefit, true)}</td>
              <td>{formatValue(row.annualUsage)}</td>
              <td>{formatValue(row.annualBenefit, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
